import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import * as styles from "./channel-topic-table.module.scss";
import EditableChannelTopic from "./editable-channel-topic";
import ChannelTopicRow from "./channel-topic-row";
import InputButton from "../../../elements/input-button";
import { makeStyleable } from "../../../helper/icon-helper";
import IconCircleAdd from "../../../dynamicicons/icon-circle-add";

interface ChannelTopicTableProps {
	className?: string;
	title: string;
	channelTopics: EditableChannelTopic[];
	setChannelTopics?: (channelTopics: EditableChannelTopic[]) => void;
	readOnly?: boolean;
}

export default function ChannelTopicTable(props: ChannelTopicTableProps) {
	const { t } = useTranslation();

	const channelTopicErrors = useMemo(() => {
		const errorMap = new Map<number, string>();

		props.channelTopics.forEach((channelTopic, i) => {
			//Check the validity of the channel
			const wirepasChannelString = channelTopic.wirepas_channel;
			if (wirepasChannelString.length > 0) {
				//Check the channel number
				const wirepasChannel = parseInt(wirepasChannelString);
				if (
					isNaN(wirepasChannel) ||
					wirepasChannel < 1 ||
					wirepasChannel > 255
				) {
					errorMap.set(
						i,
						t("settings.broker.channel_topic.channel_range_error"),
					);
					return;
				}

				//Check if the number is already taken
				if (
					props.channelTopics.some((otherChannelTopic) => {
						return (
							channelTopic !== otherChannelTopic &&
							parseInt(otherChannelTopic.wirepas_channel) ===
								wirepasChannel
						);
					})
				) {
					errorMap.set(
						i,
						t("settings.broker.channel_topic.endpoint_taken"),
					);
					return;
				}
			}
		});

		return errorMap;
	}, [props.channelTopics, t]);

	return (
		<div className={props.className}>
			<span className={styles.title}>{props.title}</span>

			{props.channelTopics.length > 0 && (
				<table className={styles.table}>
					<thead>
						<tr>
							<th className={styles.wirepasEndpointHeader}>
								{t(
									"settings.broker.channel_topic.wirepas_endpoint",
								)}
							</th>
							<th className={styles.mqttTopicHeader}>
								{t("settings.broker.channel_topic.mqtt_topic")}
							</th>
						</tr>
					</thead>

					<tbody>
						{props.channelTopics.map((channelTopic, i) => (
							<ChannelTopicRow
								key={i}
								channelTopic={channelTopic}
								errorMessage={channelTopicErrors.get(i)}
								onChange={(newChannelTopic) => {
									const newChannelTopics = [
										...props.channelTopics,
									];
									newChannelTopics[i] = newChannelTopic;
									props.setChannelTopics?.(newChannelTopics);
								}}
								onRemove={() => {
									const newChannelTopics = [
										...props.channelTopics,
									];
									newChannelTopics.splice(i, 1);
									props.setChannelTopics?.(newChannelTopics);
								}}
								readOnly={props.readOnly}
							/>
						))}
					</tbody>
				</table>
			)}

			{!props.readOnly && (
				<InputButton
					className={styles.addButton}
					icon={makeStyleable(IconCircleAdd)}
					label={t("settings.broker.add_endpoint")}
					fullWidth
					onClick={() => {
						const newChannelTopics = [...props.channelTopics];
						newChannelTopics.push({
							wirepas_channel: "",
							mqtt_topic: "",
						});
						props.setChannelTopics?.(newChannelTopics);
					}}
				/>
			)}
		</div>
	);
}
